import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Analysis, AsyncStatus, AnalysesErrorMessages, Essentials } from '@algoan/dashboard/model';
import { updateAuthInformation } from './auth-slice';

export interface AnalysesReducerState {
  analyses: Analysis[];
  analysesError: AnalysesErrorMessages | undefined;
  analysesErrorMessage?: string;
  analysesErrorDate?: string;
  analysesFetchingStatus: AsyncStatus;
  recategorisationStatus: AsyncStatus;
  essentials: Essentials | undefined;
  essentialsFetchingStatus: AsyncStatus;
}

const getInitialState = (): AnalysesReducerState => ({
  analyses: [],
  analysesError: undefined,
  analysesErrorMessage: undefined,
  analysesFetchingStatus: AsyncStatus.IDLE,
  recategorisationStatus: AsyncStatus.IDLE,
  essentials: undefined,
  essentialsFetchingStatus: AsyncStatus.IDLE,
});

const initialState = getInitialState();

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    fetchAnalysesStart: (state) => {
      state.analysesFetchingStatus = AsyncStatus.PENDING;
    },
    fetchAnalysesError: (
      state,
      action: PayloadAction<{ error: AnalysesErrorMessages; date?: string; errorMessage?: string }>,
    ) => {
      const { error, date, errorMessage } = action.payload;

      if (error !== AnalysesErrorMessages.ZERO_INCOME && error !== AnalysesErrorMessages.LOW_ACTIVITY_SCORE) {
        state.analyses = [];
      }
      state.analysesError = error;
      state.analysesErrorMessage = errorMessage;
      state.analysesErrorDate = date ?? new Date().toISOString();
      state.analysesFetchingStatus = AsyncStatus.REJECTED;
    },
    fetchAnalysesSuccess: (state, action: PayloadAction<{ analyses: Analysis[] }>) => {
      const { analyses } = action.payload;

      state.analyses = analyses;
      state.analysesError = undefined;
      state.analysesFetchingStatus = AsyncStatus.RESOLVED;
    },
    recategorisationStart: (state) => {
      state.recategorisationStatus = AsyncStatus.PENDING;
    },
    recategorisationError: (state) => {
      state.recategorisationStatus = AsyncStatus.REJECTED;
    },
    recategorisationSuccess: (state) => {
      state.recategorisationStatus = AsyncStatus.RESOLVED;
    },
    fetchEssentialsStart: (state) => {
      state.essentialsFetchingStatus = AsyncStatus.PENDING;
    },
    fetchEssentialsSuccess: (state, action: PayloadAction<{ essentials: Essentials }>) => {
      state.essentials = action.payload.essentials;
      state.essentialsFetchingStatus = AsyncStatus.RESOLVED;
    },
    fetchEssentialsError: (state) => {
      state.essentials = undefined;
      state.essentialsFetchingStatus = AsyncStatus.REJECTED;
    },
    resetAnalysesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(updateAuthInformation, (state, action) => {
      if (typeof action.payload === 'undefined') {
        // we clear the state on logout
        return getInitialState();
      }

      return state;
    });
  },
});

export const { reducer: analysisReducer } = analysisSlice;
export const {
  fetchAnalysesStart,
  fetchAnalysesSuccess,
  fetchAnalysesError,
  recategorisationStart,
  recategorisationError,
  recategorisationSuccess,
  fetchEssentialsStart,
  fetchEssentialsSuccess,
  fetchEssentialsError,
  resetAnalysesState,
} = analysisSlice.actions;
